class CFHomeHeroCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.slides = Array.from(this.querySelectorAll('[data-cf-hero-slide]'));
    this.pickerButtons = Array.from(this.querySelectorAll('[data-cf-hero-go-to]'));
    this.previousButton = this.querySelector('[data-cf-hero-previous]');
    this.nextButton = this.querySelector('[data-cf-hero-next]');
    this.status = this.querySelector('[data-cf-hero-status]');

    if (this.slides.length === 0) return;

    this.currentIndex = Math.max(
      0,
      this.slides.findIndex((slide) => !slide.hidden),
    );

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);

    this.dataset.initialized = 'true';
    this.dataset.enhanced = 'true';
    this.show(this.currentIndex, false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    delete this.dataset.initialized;
    delete this.dataset.enhanced;
  }

  handleClick(event) {
    const picker = event.target.closest('[data-cf-hero-go-to]');
    if (picker && this.contains(picker)) {
      this.show(Number.parseInt(picker.dataset.cfHeroGoTo, 10), true);
      return;
    }

    if (event.target.closest('[data-cf-hero-previous]')) {
      this.show(this.currentIndex - 1, true);
    } else if (event.target.closest('[data-cf-hero-next]')) {
      this.show(this.currentIndex + 1, true);
    }
  }

  handleKeydown(event) {
    const picker = event.target.closest('[data-cf-hero-go-to]');
    if (!picker || !this.contains(picker)) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    let targetIndex = this.currentIndex;
    if (event.key === 'ArrowLeft') targetIndex -= 1;
    if (event.key === 'ArrowRight') targetIndex += 1;
    if (event.key === 'Home') targetIndex = 0;
    if (event.key === 'End') targetIndex = this.slides.length - 1;

    this.show(targetIndex, true);
    this.pickerButtons[this.currentIndex]?.focus();
  }

  handleBlockSelect(event) {
    const blockId = event.detail?.blockId || event.target?.dataset?.blockId;
    if (!blockId) return;

    const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
    if (index >= 0) this.show(index, false);
  }

  show(requestedIndex, announce) {
    const count = this.slides.length;
    if (count === 0 || !Number.isFinite(requestedIndex)) return;

    const nextIndex = (requestedIndex + count) % count;
    this.currentIndex = nextIndex;

    this.slides.forEach((slide, index) => {
      const isCurrent = index === nextIndex;
      slide.hidden = !isCurrent;
      slide.setAttribute('aria-hidden', String(!isCurrent));
    });

    this.pickerButtons.forEach((button, index) => {
      const isCurrent = index === nextIndex;
      button.setAttribute('aria-pressed', String(isCurrent));
      button.setAttribute('aria-current', isCurrent ? 'true' : 'false');
      button.tabIndex = isCurrent ? 0 : -1;
    });

    if (this.status) {
      if (!announce) this.status.setAttribute('aria-live', 'off');
      else this.status.setAttribute('aria-live', 'polite');
      this.status.textContent = `Slide ${nextIndex + 1} of ${count}`;
    }
  }
}

class CFHomeScroller extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.track = this.querySelector('[data-cf-home-track]');
    this.previousButton = this.querySelector('[data-cf-home-previous]');
    this.nextButton = this.querySelector('[data-cf-home-next]');

    if (!this.track || !this.previousButton || !this.nextButton) return;

    this.dataset.initialized = 'true';
    this.dataset.enhanced = 'true';
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.handlePrevious = () => this.scrollByCard(-1);
    this.handleNext = () => this.scrollByCard(1);
    this.handleTrackScroll = () => this.queueControlUpdate();
    this.handleResize = () => this.queueControlUpdate();

    this.previousButton.addEventListener('click', this.handlePrevious);
    this.nextButton.addEventListener('click', this.handleNext);
    this.track.addEventListener('scroll', this.handleTrackScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });

    this.updateControls();
  }

  disconnectedCallback() {
    if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.track?.removeEventListener('scroll', this.handleTrackScroll);
    window.removeEventListener('resize', this.handleResize);
    delete this.dataset.initialized;
    delete this.dataset.enhanced;
  }

  scrollByCard(direction) {
    const firstCard = this.track.querySelector('.cf-home-scenarios__item');
    if (!firstCard) return;

    const styles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || 0);
    const distance = firstCard.getBoundingClientRect().width + gap;

    this.track.scrollBy({
      left: direction * distance,
      behavior: this.prefersReducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  queueControlUpdate() {
    if (this.animationFrame) return;

    this.animationFrame = window.requestAnimationFrame(() => {
      this.updateControls();
      this.animationFrame = null;
    });
  }

  updateControls() {
    const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const currentScroll = Math.abs(this.track.scrollLeft);
    const tolerance = 2;

    this.previousButton.disabled = currentScroll <= tolerance;
    this.nextButton.disabled = currentScroll >= maximumScroll - tolerance;
  }
}

if (!customElements.get('cf-home-hero-carousel')) {
  customElements.define('cf-home-hero-carousel', CFHomeHeroCarousel);
}

if (!customElements.get('cf-home-scroller')) {
  customElements.define('cf-home-scroller', CFHomeScroller);
}
